import React, { useState } from "react";
import { toast } from "sonner";
import { TrendingUp, Loader2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Badge, fcTone } from "@/components/kit";

export default function ProfitMode() {
  const [f, setF] = useState({ ingredients: "salmon, asparagus, potatoes, lemon", selling_price: 32, max_food_cost: 28, guests: 100, cuisine: "Modern American", service_style: "Fine Dining" });
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const upd = (k, v) => setF({ ...f, [k]: v });

  const run = async () => {
    setLoading(true); setRes(null);
    try {
      const { data } = await api.post("/profit/generate", {
        mode: "profit", ingredients: f.ingredients.split(",").map((s) => s.trim()).filter(Boolean),
        selling_price: +f.selling_price, max_food_cost: +f.max_food_cost, guests: +f.guests,
        cuisine: f.cuisine, service_style: f.service_style, target_food_cost: +f.max_food_cost,
      });
      setRes(data);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  return (
    <div>
      <PageHeader eyebrow="Reverse Engineering" title="Profit Mode"
        subtitle={"Don't ask \u201Cwhat do you want to cook?\u201D \u2014 ask \u201Cwhat do you want to earn?\u201D Chef Plated engineers dishes to hit your numbers."} />
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="matte-card p-6 space-y-4 h-fit">
          <Field label="Available ingredients" value={f.ingredients} onChange={(e) => upd("ingredients", e.target.value)} testid="profit-ingredients" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Selling price $" type="number" value={f.selling_price} onChange={(e) => upd("selling_price", e.target.value)} testid="profit-price" />
            <Field label="Max food cost %" type="number" value={f.max_food_cost} onChange={(e) => upd("max_food_cost", e.target.value)} testid="profit-maxfc" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Covers" type="number" value={f.guests} onChange={(e) => upd("guests", e.target.value)} testid="profit-covers" />
            <Field label="Cuisine" value={f.cuisine} onChange={(e) => upd("cuisine", e.target.value)} testid="profit-cuisine" />
          </div>
          <button onClick={run} disabled={loading} data-testid="profit-generate-btn" className="btn-gold w-full py-4 text-sm uppercase tracking-widest rounded-sm flex items-center justify-center gap-2">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <TrendingUp size={16} />} Engineer Dishes
          </button>
          <p className="text-white/30 text-xs">Figures are estimated plate costs, not live supplier costs.</p>
        </div>

        <div className="lg:col-span-2">
          {loading && <div className="matte-card p-14 text-center text-white/40"><Loader2 size={28} className="animate-spin text-gold mx-auto mb-3" />Engineering dishes to your targets…</div>}
          {!res && !loading && <div className="matte-card p-14 text-center text-white/40"><TrendingUp size={30} className="mx-auto mb-3 text-gold/50" />Set your constraints and generate profit-first dishes.</div>}
          {res && (
            <div className="space-y-4">
              {res.concept && <p className="text-white/60 text-sm italic">{res.concept}</p>}
              {res.dishes.map((d, i) => (
                <div key={i} className="matte-card p-6" data-testid={`profit-dish-${i}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div><h3 className="font-serif text-2xl">{d.name}</h3><p className="text-white/50 text-sm mt-1">{d.description}</p></div>
                    <Badge tone={fcTone(d.food_cost_pct, +f.max_food_cost)}>{d.food_cost_pct}% FC</Badge>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-5">
                    <Metric label="Plate Cost" value={`$${d.plate_cost}`} />
                    <Metric label="Selling Price" value={`$${d.selling_price}`} />
                    <Metric label="Food Cost" value={`${d.food_cost_pct}%`} />
                    <Metric label="Profit / Plate" value={`$${d.gross_profit}`} accent />
                    <Metric label="Projected Profit" value={`$${d.projected_profit.toLocaleString()}`} accent />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testid }) {
  return <div><label className="label-eyebrow block mb-2">{label}</label>
    <input type={type} value={value} onChange={onChange} data-testid={testid} className="w-full bg-surface border border-white/10 rounded-sm px-4 py-3 text-white/90 outline-none focus:border-gold/60" /></div>;
}
function Metric({ label, value, accent }) {
  return <div className="p-3 rounded-sm bg-black/30 border border-white/5"><p className="text-white/40 text-xs">{label}</p><p className={`font-mono text-lg mt-0.5 ${accent ? "gold-text" : "text-cream"}`}>{value}</p></div>;
}
